import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex, type FlexProps } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo } from "react";

import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  getOrderDetailsFromFulfillmentGroups,
  getOrderDetailsInfoFromRedoOrder,
} from "@redotech/redo-merchant-app-common/order-details-info";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Order } from "@redotech/redo-order-core-common/order";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PreloadedOrderInfo,
  useOrderNavigation,
} from "../fulfillment-groups/order-navigation-context";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

interface NavigationGroup {
  id: string;
  orderId: string;
  isRedoOrder: boolean;
}

export const OrderNavigation = memo(function OrderNavigation({
  currentFulfillmentGroupId,
  pb = "xl",
}: {
  currentFulfillmentGroupId: string;
  pb?: FlexProps["pb"];
}) {
  const {
    fulfillmentGroupInfo,
    orderPagePrefetchedInfo,
    resetOrderPagePrefetchedInfo,
  } = useOrderNavigation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const merchantClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);

  const currIndex = useMemo(() => {
    return fulfillmentGroupInfo.findIndex(
      (group) => group.id === currentFulfillmentGroupId,
    );
  }, [fulfillmentGroupInfo, currentFulfillmentGroupId]);

  const totalCount = useMemo(() => {
    return fulfillmentGroupInfo.length;
  }, [fulfillmentGroupInfo]);

  const getOrderDetailsFromRedoOrder = async (orderId: string | undefined) => {
    if (!orderId) {
      return undefined;
    }
    const { order } = await merchantClient.getOrder({
      orderId,
      teamId: team._id.toString(),
    });
    return getOrderDetailsInfoFromRedoOrder(order as unknown as Order);
  };

  const getOrderDetailsFromNonRedoOrder = async (
    externalOrderId: string | undefined,
  ) => {
    if (!externalOrderId) {
      return undefined;
    }
    const { fulfillmentGroups } = await rpcClient.getFulfillmentGroupsByOrderId(
      { externalOrderId },
    );
    const result = getOrderDetailsFromFulfillmentGroups(
      fulfillmentGroups,
      externalOrderId,
    );
    return result;
  };

  const navReady = useLoad(async () => {
    if (currIndex < 0 || currIndex >= fulfillmentGroupInfo.length) {
      return;
    }
    const prevGroup = fulfillmentGroupInfo[currIndex - 1];
    const nextGroup = fulfillmentGroupInfo[currIndex + 1];

    if (!prevGroup && !nextGroup) {
      return;
    }

    const prevFulfillmentGroupParams = prevGroup?.isRedoOrder
      ? { orderId: prevGroup.orderId }
      : { orderExternalId: prevGroup?.orderId };
    const nextFulfillmentGroupParams = nextGroup?.isRedoOrder
      ? { orderId: nextGroup.orderId }
      : { orderExternalId: nextGroup?.orderId };
    const getPrevOrderDetails = prevGroup?.isRedoOrder
      ? getOrderDetailsFromRedoOrder
      : getOrderDetailsFromNonRedoOrder;
    const getNextOrderDetails = nextGroup?.isRedoOrder
      ? getOrderDetailsFromRedoOrder
      : getOrderDetailsFromNonRedoOrder;

    const prevOrderId = prevGroup?.isRedoOrder
      ? prevFulfillmentGroupParams.orderId
      : prevFulfillmentGroupParams.orderExternalId;
    const nextOrderId = nextGroup?.isRedoOrder
      ? nextFulfillmentGroupParams.orderId
      : nextFulfillmentGroupParams.orderExternalId;

    const [
      prevOrderDetailsInfo,
      prevFulfillmentGroupData,
      nextOrderDetailsInfo,
      nextFulfillmentGroupData,
    ] = await Promise.allSettled([
      getPrevOrderDetails(prevOrderId),
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      rpcClient.getFulfillmentGroupsForOrder(prevFulfillmentGroupParams),
      getNextOrderDetails(nextOrderId),
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      rpcClient.getFulfillmentGroupsForOrder(nextFulfillmentGroupParams),
    ]);

    const prefetchedInfo: Record<string, PreloadedOrderInfo> = {};
    if (
      prevOrderDetailsInfo.status === "fulfilled" &&
      prevFulfillmentGroupData.status === "fulfilled" &&
      prevOrderDetailsInfo.value
    ) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const prevOrderId = fulfillmentGroupInfo[currIndex - 1].orderId;
      prefetchedInfo[prevOrderId] = {
        orderInfo: prevOrderDetailsInfo.value,
        fulfillmentGroupInfo: prevFulfillmentGroupData.value.fulfillmentGroups,
      };
    }

    if (
      nextOrderDetailsInfo.status === "fulfilled" &&
      nextFulfillmentGroupData.status === "fulfilled" &&
      nextOrderDetailsInfo.value
    ) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const nextOrderId = fulfillmentGroupInfo[currIndex + 1].orderId;
      prefetchedInfo[nextOrderId] = {
        orderInfo: nextOrderDetailsInfo.value,
        fulfillmentGroupInfo: nextFulfillmentGroupData.value.fulfillmentGroups,
      };
    }
    resetOrderPagePrefetchedInfo(prefetchedInfo);

    return true;
  }, [currIndex, fulfillmentGroupInfo, resetOrderPagePrefetchedInfo]);

  const navigate = useNavigate();
  // OMS-3209: preserve the source fulfillment view across prev/next so the
  // breadcrumb keeps pointing back to wherever the user opened the order
  // from. The param isn't needed for navigation itself — it only feeds the
  // detail page's breadcrumb logic — but losing it on every arrow click
  // would silently re-break the fix.
  const [searchParams] = useSearchParams();
  const sourceViewParam = (() => {
    const sv = searchParams.get("sourceView");
    return sv ? `&sourceView=${encodeURIComponent(sv)}` : "";
  })();

  const encodeOrderIdForNavigation = (
    orderId: string,
    isRedoOrder: boolean,
  ): string => {
    return isRedoOrder ? orderId : btoa(orderId);
  };

  const getDestinationTab = (order: NavigationGroup): string | undefined => {
    const destinationFulfillmentGroups =
      orderPagePrefetchedInfo[order.orderId]?.fulfillmentGroupInfo;
    return (
      destinationFulfillmentGroups?.find((group) => group._id === order.id)
        ?._id ?? destinationFulfillmentGroups?.[0]?._id
    );
  };

  const prev = useHandler(() => {
    if (currIndex > 0) {
      const prevOrder = fulfillmentGroupInfo[currIndex - 1];
      if (!prevOrder) {
        return;
      }
      const outboundOrderTab = getDestinationTab(prevOrder);
      if (!outboundOrderTab) {
        return;
      }
      const encodedOrderId = encodeOrderIdForNavigation(
        prevOrder.orderId,
        prevOrder.isRedoOrder,
      );
      navigate(
        `../${encodedOrderId}?outboundOrderTab=${outboundOrderTab}&isRedoOrder=${prevOrder.isRedoOrder}${sourceViewParam}`,
      );
    }
  });

  const next = useHandler(() => {
    if (currIndex < totalCount - 1) {
      const nextOrder = fulfillmentGroupInfo[currIndex + 1];
      if (!nextOrder) {
        return;
      }
      const outboundOrderTab = getDestinationTab(nextOrder);
      if (!outboundOrderTab) {
        return;
      }
      const encodedOrderId = encodeOrderIdForNavigation(
        nextOrder.orderId,
        nextOrder.isRedoOrder,
      );
      navigate(
        `../${encodedOrderId}?outboundOrderTab=${outboundOrderTab}&isRedoOrder=${nextOrder.isRedoOrder}${sourceViewParam}`,
      );
    }
  });

  const handleKeyDown = useHandler((event: KeyboardEvent) => {
    if (
      document.activeElement &&
      (document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA" ||
        document.activeElement.getAttribute("contenteditable") === "true")
    ) {
      return;
    }

    switch (event.key) {
      case "ArrowUp":
      case "ArrowLeft":
        if (currIndex > 0 && !navReady.pending && navReady.value) {
          event.preventDefault();
          prev();
        }
        break;
      case "ArrowDown":
      case "ArrowRight":
        if (currIndex < totalCount - 1 && !navReady.pending && navReady.value) {
          event.preventDefault();
          next();
        }
        break;
    }
  });

  useEffect(() => {
    addEventListener("keydown", handleKeyDown);

    return () => {
      removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  if (fulfillmentGroupInfo.length === 0) {
    return null;
  }

  return (
    <Flex align="center" dir="row" gap="xl" pb={pb}>
      <Flex align="center">
        <Flex dir="row" gap="sm">
          <Text fontSize="xs">{currIndex === -1 ? "-" : currIndex + 1}</Text>
          <Text fontSize="xs" textColor="placeholder">
            / {totalCount}
          </Text>
        </Flex>
        <RedoButton
          disabled={currIndex === 0 || navReady.pending || !navReady.value}
          hierarchy="secondary"
          IconLeading={ChevronUpIcon}
          onClick={prev}
          size="sm"
        />
        <RedoButton
          disabled={
            currIndex === totalCount - 1 || navReady.pending || !navReady.value
          }
          hierarchy="secondary"
          IconLeading={ChevronDownIcon}
          onClick={next}
          size="sm"
        />
      </Flex>
    </Flex>
  );
});
